/**
 * Payments Tab Component
 * Displays Asset's payment history with search and pagination
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '../../../../../hooks';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  InputBase,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Search as SearchIcon, Launch as LaunchIcon } from '@mui/icons-material';
import { EmptyState, LoadingSpinner, CopyableText } from '../../../../../components/common';
import { DataTablePagination } from '../../../../../components/ui';
import { UserService } from '../../../../../services/user/UserService';
import { formatDateTime } from '../../../../../utils/dateUtils';
import type { UserDetail } from '../../../../../types/user';

interface PaymentsTabProps {
  assetDetail: UserDetail;
}

interface PaymentTransaction {
  id: string;
  missionId: string;
  missionCode: string;
  missionTitle: string;
  amount: number;
  platformFee: number;
  bookingFee: number;
  ghostEarnings: number;
  currency: string;
  status: string;
  operationType: string;
  transactionAt: string | null;
}

const getStatusColor = (status: string, theme: any): string => {
  switch (status) {
    case 'Settled':
      return theme.palette.success.main;
    case 'Authorized':
      return theme.palette.warning.main;
    case 'Failed':
    case 'Voided':
      return theme.palette.error.main;
    case 'Refunded':
      return theme.palette.info.main;
    default:
      return theme.palette.text.secondary;
  }
};

export const PaymentsTab: React.FC<PaymentsTabProps> = ({ assetDetail }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const [payments, setPayments] = useState<PaymentTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchPayments = useCallback(async () => {
    try {
      setIsLoading(true);
      const userId = assetDetail.id || assetDetail.userId;
      if (!userId) {
        console.error('User ID is missing');
        setPayments([]);
        return;
      }
      const result = await UserService.getUserPayments(
        userId,
        page,
        pageSize,
        debouncedSearchValue || undefined
      );
      setPayments(result.data);
      setTotalResults(result.total);
      setTotalPages(result.totalPages);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      setPayments([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, assetDetail.id, assetDetail.userId, debouncedSearchValue]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setPage(1);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, totalResults);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing(2),
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontSize: '1rem',
          }}
        >
          Payment History
        </Typography>
        <Box
          sx={{
            position: 'relative',
            borderRadius: theme.shapes.borderRadius.md,
            backgroundColor: theme.customColors.searchBackground,
            border: `1px solid ${theme.customColors.searchBorder}`,
            width: 250,
          }}
        >
          <Box
            sx={{
              padding: theme.spacing(0, 1),
              height: '100%',
              position: 'absolute',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SearchIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
          </Box>
          <InputBase
            placeholder="Search by mission title..."
            value={searchValue}
            onChange={handleSearchChange}
            sx={{
              color: theme.palette.text.primary,
              width: '100%',
              padding: theme.spacing(1, 1, 1, 4),
              fontSize: '0.875rem',
            }}
          />
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <LoadingSpinner />
        </Box>
      ) : payments.length === 0 ? (
        <EmptyState
          title="No Payments Found"
          message={searchValue ? "No payments match your search criteria." : "This Asset has no payment records."}
        />
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Mission Code
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Mission Title
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Amount
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Date
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>
                    <CopyableText
                      text={payment.missionCode}
                      onTextClick={() => navigate(`/missions/${payment.missionId}`)}
                      sx={{ color: theme.palette.primary.main, fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary }}>
                    {payment.missionTitle}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                    ${payment.amount.toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={payment.status}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(payment.status, theme),
                        color: '#FFFFFF',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary }}>
                    {payment.transactionAt ? formatDateTime(payment.transactionAt) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Link
                      component="button"
                      onClick={() => navigate(`/finance/${payment.id}`)}
                      sx={{
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(0.5),
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      View
                      <LaunchIcon sx={{ fontSize: 14 }} />
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <DataTablePagination
            totalPages={totalPages}
            page={page}
            onPageChange={handlePageChange}
            showStats
            startIndex={startIndex}
            endIndex={endIndex}
            totalResults={totalResults}
            layout="space-between"
          />
        </>
      )}
    </Box>
  );
};
